import { useEditorStore } from "@/lib/stores/editor-store/editor-store";
import { CreativeProjectTimelineItem } from "@repo/api/creative-project";
import { IconInfoCircle } from "@tabler/icons-react";
import { useBufferingBanner } from "./useBufferingBanner";
import {
  getVoiceOverOverflowMessage,
  useVoiceOverOverflowMs,
} from "../VoiceoverTab/voiceover-overflow";

type Props = {
  timelineItems: CreativeProjectTimelineItem[];
};

export default function VideoPreviewBanners({ timelineItems }: Props) {
  const isBuffering = useBufferingBanner();
  const isPlaying = useEditorStore(
    (state) => state.videoPreviewState.isPlaying,
  );

  const currentVoiceOver = useEditorStore((state) =>
    state.service.getCurrentVoiceOver(),
  );
  const voiceOverOverflowMs = useVoiceOverOverflowMs(
    currentVoiceOver?.durationMs,
  );

  const hasNoAnimations =
    timelineItems.length > 0 &&
    timelineItems.every(
      (timelineItem) => !timelineItem.animatedImageExternalId,
    );
  const hasAnimations = timelineItems.some(
    (timelineItem) => timelineItem.animatedImageExternalId,
  );

  if (!isPlaying) {
    return null;
  }

  if (isBuffering) {
    return <BufferingBanner />;
  }

  if (hasNoAnimations) {
    return <AnimateImagesBanner />;
  }

  if (hasAnimations && voiceOverOverflowMs > 0) {
    return <Banner text={getVoiceOverOverflowMessage(voiceOverOverflowMs)} />;
  }

  return null;
}

function AnimateImagesBanner() {
  return <Banner text="Animate images to add movement" />;
}

function BufferingBanner() {
  return <Banner text="Wait for the video to load for smooth playback" />;
}

type BannerProps = {
  text: string;
};

function Banner({ text }: BannerProps) {
  return (
    <div className="pointer-events-none absolute bottom-0 left-0 z-20 flex w-full justify-center p-4">
      <div className="flex animate-fade-in-fast items-center gap-2 rounded-lg bg-toast-info-background/90 px-3 py-2 text-sm text-toast-info-text">
        <IconInfoCircle size={16} className="shrink-0" />
        <p>{text}</p>
      </div>
    </div>
  );
}
